import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { motion } from 'framer-motion';
import API_BASE_URL from '../config/api';
import { FiDownload, FiFileText, FiSearch } from 'react-icons/fi';

const Resources = () => {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTag, setSelectedTag] = useState('ALL');
    const [selectedGrade, setSelectedGrade] = useState('ALL');

    useEffect(() => {
        const fetchResources = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/manage_resources.php`);
                const data = await response.json();
                if (data.status === 'success') {
                    setResources(data.data);
                }
            } catch (error) {
                console.error("Error fetching resources:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchResources();
    }, []);

    const uniqueTags = ['ALL', ...new Set(resources.map(res => res.tag || 'General'))];
    const uniqueGrades = ['ALL', ...new Set(resources.map(res => res.grade || 'All Grades'))];

    const filteredResources = resources.filter(res => {
        const matchesSearch = res.title.toLowerCase().includes(searchTerm.toLowerCase()) || res.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTag = selectedTag === 'ALL' || (res.tag || 'General') === selectedTag;
        const matchesGrade = selectedGrade === 'ALL' || (res.grade || 'All Grades') === selectedGrade;
        return matchesSearch && matchesTag && matchesGrade;
    });

    return (
        <div className="bg-slate-50 min-h-screen">
            <Navbar />
            
            {/* Header */}
            <div className="pt-32 pb-20 bg-white relative overflow-hidden">
                {/* Brand Background Elements */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500 rounded-full filter blur-[100px] translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600 rounded-full filter blur-[100px] -translate-x-1/2 translate-y-1/2"></div>
                </div>
                
                <div className="container mx-auto px-4 relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-block px-4 py-1 rounded-full bg-orange-100 text-orange-600 text-xs font-bold uppercase tracking-widest mb-4"
                    >
                        Academic Center
                    </motion.div>
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-6xl font-black text-slate-900 mb-6 uppercase tracking-tight"
                    >
                        LEARNING <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">RESOURCES</span>
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-600 max-w-2xl mx-auto font-medium"
                    >
                        Access our comprehensive collection of PDF study materials, guides, and academic resources.
                    </motion.p>
                </div>
            </div>

            {/* Search and Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="max-w-7xl mx-auto">
                    <div className="relative mb-6 max-w-4xl mx-auto">
                        <FiSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
                        <input 
                            type="text" 
                            placeholder="Search resources by title or description..." 
                            className="w-full pl-16 pr-6 py-5 rounded-2xl border-none shadow-xl shadow-slate-200 focus:ring-2 focus:ring-orange-500 outline-none text-lg font-medium"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {!loading && (
                        <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-12 bg-white p-4 rounded-3xl shadow-sm border border-slate-100">
                            
                            {/* Grade Filters */}
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-2">Grade:</span>
                                    {uniqueGrades.map(grade => (
                                        <button
                                            key={grade}
                                            onClick={() => setSelectedGrade(grade)}
                                            className={`px-4 py-2 rounded-xl text-xs font-black transition-all uppercase tracking-widest ${
                                                selectedGrade === grade 
                                                    ? 'bg-orange-500 text-white shadow-md shadow-orange-200' 
                                                    : 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                                            }`}
                                        >
                                            {grade}
                                        </button>
                                    ))}
                                </div>

                            <div className="hidden md:block w-px h-8 bg-slate-200"></div>

                            {/* Tag Filters */}
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-2">Category:</span>
                                    {uniqueTags.map(tag => (
                                        <button
                                            key={tag}
                                            onClick={() => setSelectedTag(tag)}
                                            className={`px-5 py-2.5 rounded-xl text-xs font-black transition-all uppercase tracking-widest ${
                                                selectedTag === tag 
                                                    ? 'bg-slate-900 text-white shadow-md shadow-slate-200' 
                                                    : 'bg-slate-50 text-slate-600 border border-slate-100 hover:bg-slate-100'
                                            }`}
                                        >
                                            {tag}
                                        </button>
                                    ))}
                                </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                        </div>
                    ) : filteredResources.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {filteredResources.map((res, index) => (
                                <motion.div
                                    key={res.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white p-6 rounded-3xl shadow-lg shadow-slate-200/50 border border-slate-100 hover:border-orange-200 transition-all duration-300 group relative overflow-hidden flex flex-col h-full"
                                >
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/10 to-transparent rounded-bl-[100px] -mr-4 -mt-4 transition-transform group-hover:scale-110 duration-700"></div>
                                    
                                    <div className="flex flex-col gap-4 relative z-10 flex-1">
                                        <div className="flex justify-between items-start">
                                            {res.image_path ? (
                                                <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-50 border border-slate-100 shadow-sm shrink-0">
                                                    <img src={`${API_BASE_URL}/../${res.image_path}`} alt="Icon" className="w-full h-full object-contain p-1" />
                                                </div>
                                            ) : (
                                                <div className="p-3 bg-orange-50 rounded-2xl text-orange-600 group-hover:bg-orange-600 group-hover:text-white transition-all duration-300 shadow-inner">
                                                    <FiFileText size={24} />
                                                </div>
                                            )}
                                            <div className="flex flex-col gap-1 items-end shrink-0">
                                                <span className="px-3 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full text-[9px] font-black uppercase tracking-widest shadow-md">
                                                    {res.grade || 'All Grades'}
                                                </span>
                                                <span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-[9px] font-black uppercase tracking-widest">
                                                    {res.tag || 'General'}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex flex-col flex-1 mt-2">
                                            <h3 className="text-lg font-black text-slate-900 group-hover:text-orange-600 transition-colors mb-2 leading-snug">
                                                {res.title}
                                            </h3>
                                            <p className="text-slate-500 mb-6 line-clamp-3 text-sm font-medium leading-relaxed">
                                                {res.description}
                                            </p>
                                            <a 
                                                href={`${API_BASE_URL}/../${res.file_path}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="inline-flex w-full justify-center items-center gap-2 bg-slate-900 text-white px-4 py-3.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-orange-600 transition-all shadow-xl shadow-slate-200 mt-auto"
                                            >
                                                <FiDownload size={16} /> Download
                                            </a>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <div className="inline-block p-6 bg-slate-100 rounded-full text-slate-400 mb-4">
                                <FiFileText size={48} />
                            </div>
                            <p className="text-slate-500 text-xl font-medium">No resources found matching your search.</p>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Resources;
